import React, {
  useState,
  useEffect
} from "react";

import axios from "axios";

import VisionNavbar from "./VisionNavbar";

import {
  FaFire,
  FaTrophy,
  FaTasks
} from "react-icons/fa";

import "./Train.css";


const Train = () => {

  const email =
    localStorage.getItem("user_email");


  /* =====================================================
     HIDE MAIN NAVBAR / FOOTER
  ===================================================== */

  useEffect(() => {

    const navbar =
      document.querySelector(
        "header, .navbar"
      );

    const footer =
      document.querySelector(
        "footer"
      );


    if (navbar)
      navbar.style.display = "none";

    if (footer)
      footer.style.display = "none";


    return () => {

      if (navbar)
        navbar.style.display = "";

      if (footer)
        footer.style.display = "";

    };

  }, []);


  /* =====================================================
     STATES
  ===================================================== */

  const [
    dashboard,
    setDashboard
  ] = useState(null);


  const [
    journey,
    setJourney
  ] = useState(null);


  const [
    leaderboard,
    setLeaderboard
  ] = useState([]);


  const [
    loading,
    setLoading
  ] = useState(true);


  /* =====================================================
     FETCH DATA
  ===================================================== */

  useEffect(() => {

    if (!email) {

      setLoading(false);

      return;

    }

    fetchData();

  }, [email]);


  const fetchData = async () => {

    try {

      setLoading(true);


      const [
        dashboardRes,
        journeyRes,
        leaderboardRes
      ] = await Promise.all([

        axios.get(
          `/api/dashboard/${encodeURIComponent(email)}`
        ),

        axios.get(
          `/api/journey/${encodeURIComponent(email)}`
        ),

        axios.get(
          "/api/leaderboard/train"
        )

      ]);


      setDashboard(
        dashboardRes.data
      );


      setJourney(
        journeyRes.data?.journey ||
        journeyRes.data
      );


      setLeaderboard(
        leaderboardRes.data || []
      );

    }

    catch (err) {

      console.error(
        "Train page error:",
        err
      );

    }

    finally {

      setLoading(false);

    }

  };


  /* =====================================================
     LOADING
  ===================================================== */

  if (loading) {

    return (

      <div className="train-loading">

        Loading...

      </div>

    );

  }


  /* =====================================================
     BACKEND CURRENT LEVEL
  ===================================================== */

  const currentLevel =
    Number(
      journey?.currentLevel
    ) || 1;


  /* =====================================================
     LEVEL 1 COMPLETION
     
     IMPORTANT:
     Train page always shows Level 2.
     
     Level 2 opens ONLY when all Level 1
     requirements are completed.
  ===================================================== */

  const level1Completed =

    journey?.level1?.baselineTests === true &&

    journey?.level1?.visionBlueprint === true &&

    journey?.level1?.communityJoined === true;


  /* =====================================================
     LEVEL 2 COMPLETION
  ===================================================== */

  const level2Completed =

    journey?.level2?.eyeHealthCourse === true &&

    (
      journey?.level2?.practiceDays ||
      0
    ) >= 30 &&

    (
      journey?.level2?.communityCallsCount ||
      0
    ) >= 2 &&

    journey?.level2?.firstProgressCheck === true;


  /* =====================================================
     LEVEL INFORMATION
  ===================================================== */

  const level2Info = {

    name: "Explorer",

    mission:
      "Complete your Eye Health journey.",

    button:
      "START COURSE"

  };


  /* =====================================================
     LEVEL 2 TASKS
     
     ALWAYS SHOW LEVEL 2 TASKS.
     
     If Level 1 incomplete:
       → LOCKED
     
     If Level 1 complete:
       → OPEN
  ===================================================== */

  const level2Tasks = [

    {
      label:
        "Eye Health Course",

      completed:
        journey?.level2?.eyeHealthCourse === true,

      auto:
        true
    },


    {
      label:
        "30 Day Practice",

      completed:
        (
          journey?.level2?.practiceDays ||
          0
        ) >= 30,

      auto:
        false,

      field:
        "practiceDays"
    },


    {
      label:
        "Community Calls",

      completed:
        (
          journey?.level2?.communityCallsCount ||
          0
        ) >= 2,

      auto:
        false,

      field:
        "communityCallsCount"
    },


    {
      label:
        "First Progress Check",

      completed:
        journey?.level2?.firstProgressCheck === true,

      auto:
        false,

      field:
        "firstProgressCheck"
    }

  ];


  /* =====================================================
     DISPLAYED TASKS
     
     TRAIN PAGE = LEVEL 2 ONLY
  ===================================================== */

  const tasks =
    level2Tasks;


  /* =====================================================
     TASK COUNTS
     
     When Level 1 is locked, don't count Level 2
     as active progress.
  ===================================================== */

  const completedTasks =
    level1Completed
      ? tasks.filter(
          task =>
            task.completed
        ).length
      : 0;


  const pendingTasks =
    level1Completed
      ? tasks.length -
        completedTasks
      : tasks.length;


  const progressPercent =
    level1Completed

      ? Math.round(
          (
            completedTasks /
            tasks.length
          ) * 100
        )

      : 0;


  /* =====================================================
     UPDATE LEVEL 2 MANUAL TASK
  ===================================================== */

  const updateTask =
    async (task) => {

      /* -----------------------------------------------
         LEVEL 1 MUST BE COMPLETE FIRST
      ----------------------------------------------- */

      if (!level1Completed) {

        return;

      }


      /* -----------------------------------------------
         AUTOMATIC TASK
      ----------------------------------------------- */

      if (task.auto) {

        return;

      }


      try {

        let value = true;


        /* ---------------------------------------------
           COMMUNITY CALLS
        --------------------------------------------- */

        if (
          task.field ===
          "communityCallsCount"
        ) {

          value = 2;

        }


        /* ---------------------------------------------
           30 DAY PRACTICE
        --------------------------------------------- */

        else if (
          task.field ===
          "practiceDays"
        ) {

          value = 30;

        }


        /* ---------------------------------------------
           BOOLEAN TASKS
        --------------------------------------------- */

        else {

          value = true;

        }


        console.log(
          "Updating Level 2 journey:",
          {
            email,
            section: "level2",
            field: task.field,
            value
          }
        );


        /* ---------------------------------------------
           BACKEND UPDATE
        --------------------------------------------- */

        const response =
          await axios.patch(

            "/api/journey/update",

            {

              email,

              section:
                "level2",

              field:
                task.field,

              value

            }

          );


        console.log(
          "Journey update response:",
          response.data
        );


        /* ---------------------------------------------
           REFRESH
        --------------------------------------------- */

        await fetchData();

      }

      catch (err) {

        console.error(
          "Level 2 task update error:",
          err
        );

      }

    };


  /* =====================================================
     MAIN BUTTON
  ===================================================== */

  const handleMainButton =
    () => {

      /* -----------------------------------------------
         LEVEL 1 NOT COMPLETE
      ----------------------------------------------- */

      if (!level1Completed) {

        window.location.href =
          "/measure";

        return;

      }


      /* -----------------------------------------------
         LEVEL 1 COMPLETE
         OPEN LEVEL 2 COURSE
      ----------------------------------------------- */

      window.open(

        "https://learn.forgeyourpeak.in/web/courses",

        "_blank"

      );

    };


  /* =====================================================
     RENDER
  ===================================================== */

  return (

    <div className="train-page">


      {/* =================================================
          NAVBAR
      ================================================= */}

      <VisionNavbar
        active="train"
      />


      {/* =================================================
          HERO
      ================================================= */}

      <section className="train-hero">


        {/* =================================================
            LEFT
        ================================================= */}

        <div className="train-hero-left">


          <h1>

            Vision Ignite
            Journey

          </h1>


          <p>

            {level2Info.mission}

          </p>


          {/* =================================================
              LEVEL 2 JOURNEY CARD
          ================================================= */}

          <div className="journey-card">


            <h3>

              Current Status

            </h3>


            <h2>

              Level 2 Explorer

            </h2>


            {/* =================================================
                LEVEL 2 LOCK MESSAGE
            ================================================= */}

            {!level1Completed && (

              <div
                className="level2-lock-message"
              >

                🔒

                <strong>
                  Level 2 Locked
                </strong>

                <span>
                  Complete all Level 1 requirements
                  to unlock Explorer.
                </span>

              </div>

            )}


            {/* =================================================
                LEVEL 2 TASKS
            ================================================= */}

            <div
              className={
                !level1Completed
                  ? "level2-tasks locked"
                  : "level2-tasks"
              }
            >

              {

                tasks.map(

                  (
                    task,
                    index
                  ) => (

                    <div

                      key={index}

                      className={`
                        task-row
                        ${
                          !level1Completed
                            ? "task-row-locked"
                            : ""
                        }
                      `}

                    >


                      {/* =====================================
                          TASK STATUS
                      ===================================== */}

                      {

                        task.auto

                          ?

                          (

                            <span
                              className="task-status-icon"
                            >

                              {

                                task.completed

                                  ? "✅"

                                  : "⏳"

                              }

                            </span>

                          )

                          :

                          (

                            <input

                              type="checkbox"

                              checked={
                                task.completed
                              }

                              disabled={
                                !level1Completed
                              }

                              onChange={() =>
                                updateTask(
                                  task
                                )
                              }

                            />

                          )

                      }


                      <span>

                        {
                          task.label
                        }

                      </span>


                      {/* =====================================
                          STATUS
                      ===================================== */}

                      {

                        task.completed &&
                        level1Completed && (

                          <span
                            className="task-completed-text"
                          >

                            Completed

                          </span>

                        )

                      }


                      {

                        !level1Completed && (

                          <span
                            className="task-locked-text"
                          >

                            🔒 Locked

                          </span>

                        )

                      }

                    </div>

                  )

                )

              }

            </div>


            {/* =================================================
                PROGRESS
            ================================================= */}

            <div
              className="progress-section"
            >


              <div
                className="progress-track"
              >

                <div

                  className="progress-fill"

                  style={{

                    width:
                      `${progressPercent}%`

                  }}

                />

              </div>


              <div
                className="progress-footer"
              >

                <span>

                  {
                    completedTasks
                  }

                  /

                  {
                    tasks.length
                  }

                  {" "}
                  Complete

                </span>


                <span>

                  {
                    progressPercent
                  }%

                </span>

              </div>

            </div>


            {/* =================================================
                MAIN BUTTON
            ================================================= */}

            <button

              className={
                level1Completed
                  ? "train-main-btn"
                  : "train-main-btn locked"
              }

              onClick={
                handleMainButton
              }

            >

              {

                level1Completed

                  ? "START COURSE"

                  : "🔒 LEVEL 2 LOCKED"

              }

            </button>


          </div>

        </div>


        {/* =================================================
            RIGHT SIDE
        ================================================= */}

        <div
          className="train-hero-right"
        >


          {/* =================================================
              DAILY STREAK
          ================================================= */}

          <div
            className="stat-card"
          >

            <div
              className="stat-icon fire"
            >

              <FaFire />

            </div>


            <div>

              <h3>

                {
                  dashboard?.dailyStreak ||
                  0
                }

              </h3>


              <p>

                Daily Streak

              </p>

            </div>

          </div>


          {/* =================================================
              CURRENT LEVEL
          ================================================= */}

          <div
            className="stat-card"
          >

            <div
              className="stat-icon level"
            >

              <FaTrophy />

            </div>


            <div>

              <h3>

                Level 2

              </h3>


              <p>

                Explorer

              </p>

            </div>

          </div>


          {/* =================================================
              PENDING TASKS
          ================================================= */}

          <div
            className="stat-card"
          >

            <div
              className="stat-icon pending"
            >

              <FaTasks />

            </div>


            <div>

              <h3>

                {
                  pendingTasks
                }

              </h3>


              <p>

                Pending Tasks

              </p>

            </div>

          </div>


          {/* =================================================
              REMAINING TASKS
          ================================================= */}

          <div
            className="pending-card"
          >

            <h3>

              Remaining Tasks

            </h3>


            {

              !level1Completed ? (

                <div
                  className="pending-item"
                >

                  🔒 Complete Level 1
                  to unlock Level 2

                </div>

              ) : (

                tasks

                  .filter(
                    task =>
                      !task.completed
                  )

                  .map(

                    (
                      task,
                      index
                    ) => (

                      <div

                        key={index}

                        className="pending-item"

                      >

                        ⬜{" "}

                        {
                          task.label
                        }

                      </div>

                    )

                  )

              )

            }


            {

              level1Completed &&
              pendingTasks === 0 && (

                <div
                  className="pending-item"
                >

                  🎉 All Tasks Completed

                </div>

              )

            }

          </div>


        </div>


      </section>


      {/* =================================================
          PATH TO MASTERY
      ================================================= */}

      <section
        className="mastery-section"
      >


        <div
          className="mastery-header"
        >

          <h2>

            PATH TO MASTERY

          </h2>

        </div>


        <div
          className="mastery-grid"
        >

          {

            [

              {
                level: 1,
                name: "Aware",
                requirements: [
                  "Baseline Assessment",
                  "Vision Blueprint",
                  "Community Access"
                ]
              },

              {
                level: 2,
                name: "Explorer",
                requirements: [
                  "Eye Health Course",
                  "30 Day Practice",
                  "Community Calls",
                  "First Progress Check"
                ]
              },

              {
                level: 3,
                name: "Adapter",
                requirements: [
                  "Improve One Metric",
                  "60 Day Evaluation",
                  "Progress Story",
                  "Practice Streak"
                ]
              },

              {
                level: 4,
                name: "Thriver",
                requirements: [
                  "90 Day Evaluation",
                  "Improve 3+ Metrics",
                  "Reduce Glasses",
                  "Help Member"
                ]
              }

            ].map(

              (item) => {

                const isCurrent =
                  currentLevel ===
                  item.level;


                const isCompleted =
                  currentLevel >
                  item.level;


                const isLocked =
                  currentLevel <
                  item.level;


                return (

                  <div

                    key={
                      item.level
                    }

                    className={`
                      mastery-card
                      ${
                        isCurrent
                          ? "current"
                          : isCompleted
                          ? "completed"
                          : "locked"
                      }
                    `}

                  >

                    {

                      isCurrent && (

                        <div
                          className="current-badge"
                        >

                          CURRENT LEVEL

                        </div>

                      )

                    }


                    <span
                      className="mastery-level"
                    >

                      LEVEL{" "}

                      {
                        item.level
                      }

                    </span>


                    <h3>

                      {
                        item.name
                      }

                    </h3>


                    <ul>

                      {

                        item.requirements.map(

                          (
                            req,
                            index
                          ) => (

                            <li
                              key={index}
                            >

                              {
                                req
                              }

                            </li>

                          )

                        )

                      }

                    </ul>


                    {

                      isCompleted && (

                        <div
                          className="badge-unlocked"
                        >

                          🏅 BADGE UNLOCKED

                        </div>

                      )

                    }


                    {

                      isLocked && (

                        <div
                          className="mastery-lock-message"
                        >

                          🔒 Complete previous
                          level

                        </div>

                      )

                    }


                    <div
                      className="mastery-footer"
                    >

                      {

                        isCompleted && (

                          <span
                            className="completed-status"
                          >

                            ✅ COMPLETED

                          </span>

                        )

                      }


                      {

                        isCurrent && (

                          <span
                            className="progress-status"
                          >

                            🔥 IN PROGRESS

                          </span>

                        )

                      }


                      {

                        isLocked && (

                          <span
                            className="locked-status"
                          >

                            🔒 LOCKED

                          </span>

                        )

                      }

                    </div>

                  </div>

                );

              }

            )

          }

        </div>

      </section>


      {/* =================================================
          TOP OPERATORS
      ================================================= */}

      <section
        className="operators-section"
      >

        <div
          className="operators-header"
        >

          <h2>

            📊 TOP OPERATORS

          </h2>

        </div>


        <div
          className="operators-card"
        >

          <div
            className="operators-table-header"
          >

            <span>
              RANK
            </span>

            <span>
              OPERATOR
            </span>

            <span>
              LEVEL
            </span>

          </div>


          {

            leaderboard.map(

              (
                user,
                index
              ) => (

                <div

                  key={
                    user._id ||
                    index
                  }

                  className="operator-row"

                >

                  <span

                    className={`
                      rank
                      ${
                        index === 0
                          ? "first"
                          : index === 1
                          ? "second"
                          : "third"
                      }
                    `}

                  >

                    #

                    {

                      String(
                        index + 1
                      ).padStart(
                        2,
                        "0"
                      )

                    }

                  </span>


                  <span>

                    {
                      user.name
                    }

                  </span>


                  <span>

                    LVL{" "}

                    {
                      user.level
                    }{" "}

                    {
                      user.levelName
                    }

                  </span>

                </div>

              )

            )

          }

        </div>

      </section>


    </div>

  );

};


export default Train;